'use client';

import { useLeadGenerationInteraction } from 'components/analytics/hooks/useLeadGenerationInteraction';
import { useBreakpoint } from 'hooks/useBreakpoints';
import { LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { trackNavigationBarInteraction } from 'components/analytics/eventHandlerTracking';
import BaseLink, { BaseLinkProps } from '../Link/BaseLink';
import { constructHref } from 'utils/linkHelpers';
import { InternalPage } from 'libs/contentful/types';

interface NavLinkProps extends Omit<BaseLinkProps, 'href'> {
  href?: string;
  internalPage?: InternalPage;
  level: number;
  cleanLinkText?: string;
  fireOnATag?: boolean;
}

function NavLink({
  children,
  href: providedHref,
  level,
  internalPage,
  cleanLinkText,
  fireOnATag,
  ...props
}: NavLinkProps) {
  // Construct the final href using the shared utility function
  const href = constructHref(providedHref, internalPage) || '/';

  const { isAboveMd } = useBreakpoint('md');
  const screen = isAboveMd ? 'desktop' : 'mobile';

  const trackNavigation = useLeadGenerationInteraction({
    interaction_type: LEAD_GENERATION_INTERACTION_TYPES.NAVIGATION_BAR_INTERACTION,
    interaction_details: {
      location: href,
      action_detail: cleanLinkText ?? children?.toString() ?? '',
    },
  });

  const createAnalyticsProps = (action: string) => ({
    additional_data: [
      {
        key: 'action',
        value: action,
      },
      {
        key: 'level',
        value: level,
      },
      {
        key: 'screen',
        value: screen,
      },
    ],
  });

  const fireEventRouterTracking = (action: string) => {
    trackNavigationBarInteraction({
      button_label: cleanLinkText ?? children?.toString() ?? '',
      action,
      level: level.toString(),
    });
  };

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>): void {
    // Only check if fireOnATag is enabled and the actual clicked element wasn't the anchor
    if (fireOnATag && e.target !== e.currentTarget) return;

    // Track the click interaction
    trackNavigation(createAnalyticsProps('click'));
    fireEventRouterTracking('click');
    // Let the link navigate naturally - no need to prevent default
  }

  function handleMouseEnter(e: React.MouseEvent<HTMLAnchorElement>): void {
    // Only check if fireOnATag is enabled and the actual hovered element wasn't the anchor
    if (fireOnATag && e.target !== e.currentTarget) return;

    // Track the hover interaction
    trackNavigation(createAnalyticsProps('hover'));
    fireEventRouterTracking('hover');
  }

  return (
    <BaseLink href={href} onClick={handleClick} onMouseEnter={handleMouseEnter} {...props}>
      {children}
    </BaseLink>
  );
}

export default NavLink;
